import type { WishlistLineItemsFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { getBridge } from '@packages/shared/src/utils/getBridge';
import { EditInWishlistFragmentFragmentDoc } from '@packages/gql/generated/shopping/EditInWishlistFragmentFragmentDoc';
import { WishlistLineItemsFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistLineItemsFragmentFragmentDoc';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { EditInWishlistDocument } from '@packages/gql/generated/shopping/EditInWishlistDocument';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinUpdateWishList } from '@packages/tracking/src/types/events';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { buildGlycerinWishlistProduct } from '../helpers/buildGlycerinObject/buildGlycerinObject';
import { editInWishlistMessage } from './messages';

/* GraphQL */ `
  fragment EditInWishlistFragment on Product {
    sku
    availability {
      isOrderable
      state
    }
  }
`;

export interface EditInWishlistButtonProps {
  data: FragmentType<typeof EditInWishlistFragmentFragmentDoc>;
  prevSku: WishlistLineItemsFragmentFragment['sku'];
  /**
   * Please use for miniDV
   * onClose function to close the miniDV
   */
  onClose?: () => void;
}

export const EditInWishlistButton = ({
  prevSku,
  data: productData,
  onClose,
}: EditInWishlistButtonProps) => {
  const product = getFragmentData(EditInWishlistFragmentFragmentDoc, productData);
  const {
    sku: newSku,
    availability: { state, isOrderable },
  } = product;

  const dispatchGlycerin = useTracking();
  const { formatMessage, locale } = useIntl();
  const { addNotification } = useNotification();
  const [editInWishlistState, executeEditInWishlistMutation] =
    useAuthMutation(EditInWishlistDocument);
  const editWishlistHandler = async () => {
    if (prevSku === newSku) {
      addNotification({
        message: formatMessage(editInWishlistMessage.sameSkuMessage),
        severity: 'warning',
        duration: 5000,
      });
      return; // no change
    }
    if (editInWishlistState.fetching || !prevSku || !newSku) return;

    try {
      const addToWishlistResult = await executeEditInWishlistMutation({
        locale,
        oldSku: prevSku,
        newSku,
        // if addOnlyIfFound is true, the newSku will be added to the wishlist only if the oldSku is found
        // if addOnlyIfFound is false, the newSku will be added to the wishlist regardless of the oldSku ensuring that we have a product in the wishlist
        addOnlyIfFound: false,
        unspecified: state === 'UNSELECTED',
      });

      if (
        addToWishlistResult.error ||
        addToWishlistResult.data?.replaceInWishlist.success === false
      ) {
        throw new Error('Error during mutation execution');
      }
      const fragmentData = getFragmentData(
        WishlistLineItemsFragmentFragmentDoc,
        addToWishlistResult.data?.replaceInWishlist.lineItems,
      );
      // success section
      getBridge().updateWishlist();
      const updatedSkuInWishlist = fragmentData?.find((item) => item.sku === newSku);
      if (updatedSkuInWishlist) {
        dispatchGlycerin<GTMEventGlycerinUpdateWishList>({
          event: 'UpdateWishList',
          UpdateWishListData: {
            updatedProducts: [buildGlycerinWishlistProduct(updatedSkuInWishlist)],
          },
        });
      }
      addNotification({
        message: formatMessage(editInWishlistMessage.successMessage),
        severity: 'success',
        duration: 5000,
      });
      // close the dialog
      onClose?.();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      addNotification({
        message: formatMessage(editInWishlistMessage.failedMessage),
        severity: 'error',
        duration: 5000,
      });
    }
  };
  const isAvailable = state !== 'UNSELECTED' ? isOrderable : true;
  const isLoading = editInWishlistState.fetching;
  const customButtonText = ((): MessageDescriptor => {
    if (!isAvailable) return editInWishlistMessage.soldOut;
    return editInWishlistMessage.buttonText;
  })();
  return (
    <Button
      className="w-full min-w-[15rem] whitespace-nowrap"
      size="large"
      onClick={editWishlistHandler}
      color="primary"
      data-testid="edit-wishlist-action"
      disabled={!isAvailable || isLoading}
    >
      {isLoading ? <CircularProgress /> : formatMessage(customButtonText)}
    </Button>
  );
};
